import { ApolloClient } from 'apollo-client';
import { from, split, type ApolloLink } from 'apollo-link';
import { getMainDefinition } from 'apollo-utilities';
import { HttpLink } from 'apollo-link-http';
import { IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';

import { expValEquals } from '@confluence/feature-experiments';

import fragmentMatcherAGGTypes from './types/fragmentMatcherAGGTypes.json';
import { createOnErrorLink } from './links/OnErrorLink.ts';
import { SSRTestLink } from './links/SSRTestLink.ts';
import { SSRLink } from './links/SSRLink.ts';
import { createWrappedFetch } from './createWrappedFetch.ts';
import { getAGGWebSocketLinkInstance } from './links/AGGWebSocketLink/AGGWebSocketLink.ts';
import { AttachExtensionsLink } from './links/AGGWebSocketLink/AttachExtensionsLink.ts';
import { SSRDataLink } from './links/SSRServerLink.ts';
import { SSRClientLink } from './links/SSRClientLink.ts';
import { BroadcastConfigurableInMemoryCache } from './broadcastConfigurableInMemoryCache.ts';
import { requestIdLink } from './links/RequestIdLink.ts';

let apolloClient: ApolloClient<Object> | null = null;
const wrappedFetch = createWrappedFetch({ isForAggOnly: true });
export const SSR_AGG_NAMESPACE = 'agg';

const fragmentMatcher = new IntrospectionFragmentMatcher({
	introspectionQueryResultData: {
		__schema: {
			types: fragmentMatcherAGGTypes,
		},
	},
});

export const apolloAGGCache = new BroadcastConfigurableInMemoryCache({ fragmentMatcher });

// export for testing
export const clearAGGClient = (): void => {
	apolloClient = null;
};

export const getAGGClient = (): ApolloClient<object> => {
	// Use mock gateway Apollo client in tests
	if ((window as any).MOCK_GATEWAY_APOLLO_CLIENT) {
		return (window as any).MOCK_GATEWAY_APOLLO_CLIENT;
	}

	if (!apolloClient) {
		apolloClient = createApolloClient();
		(window as any).GLOBAL_APOLLO_AGG_CLIENT = apolloClient;
	}
	return apolloClient;
};

const createLinks = () => {
	const links = [createOnErrorLink()];
	const subscriptionLinks: ApolloLink[] = [];

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		links.push(SSRDataLink(SSR_AGG_NAMESPACE), SSRLink());
		if (process.env.CLOUD_ENV === 'staging' || process.env.CLOUD_ENV === 'branch') {
			links.push(SSRTestLink());
		}
	} else {
		if (expValEquals('cfe_use_persisted_queries_apollo', 'isEnabled', true)) {
			links.push(requestIdLink);
		}
		links.push(SSRClientLink(SSR_AGG_NAMESPACE, apolloAGGCache));
		subscriptionLinks.push(AttachExtensionsLink);
		subscriptionLinks.push(getAGGWebSocketLinkInstance());
	}

	links.push(
		new HttpLink({
			credentials: 'include',
			uri: '/gateway/api/graphql',
			fetch: wrappedFetch,
		}),
	);

	return split(
		// Condition for handling subscriptions
		// we don't want to use subscriptions in SSR mode
		({ query }) => {
			const definition = getMainDefinition(query);
			return (
				definition?.kind === 'OperationDefinition' &&
				definition?.operation === 'subscription' &&
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				!Boolean(process.env.REACT_SSR)
			);
		},
		// WebSocket link for subscriptions
		from(subscriptionLinks),
		// HTTP link for non-subscription operations
		from(links),
	);
};

const createApolloClient = (): ApolloClient<object> => {
	if (typeof window['__APOLLO_AGG_STATE__'] === 'object') {
		apolloAGGCache.restore(window['__APOLLO_AGG_STATE__']);
	}
	const client = new ApolloClient({
		// Prevent cache and network query to refresh when they are already preloaded on server-side.
		// Refreshing data will cause component to fallback to loading state.
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		ssrMode: Boolean(process.env.REACT_SSR),
		connectToDevTools: process.env.NODE_ENV !== 'production',
		link: from([createLinks()]),
		cache: apolloAGGCache,
	});

	// This is not actual API from Apollo. This field is read in next/packages/ssr-app/src/module-replacements/apollo-client.js
	(client as any).__isAGGClient = true;
	return client;
};
